import { INcpersPensionInputs } from 'app/calcs/customers/ncpers/current'
import { INcpersPriorPensionInputs } from 'app/calcs/customers/ncpers/prior'
import { IMonthlyPensionPayments, SimplePensionFormOfPayment, SimplePensionFormOfPaymentKinds } from 'app/calcs/pensions/interfaces'
import { ActionCreator } from 'app/redux/ActionCreator'
import { ActionTypes, INcpersOpAction, INcpersOpState } from 'app/redux/Customers/ncpers/reducer'
import { ageAt, IRetirementPerson, isSamePersonForSSA, personHasRetired, Profile } from 'app/redux/Profile/model'
import { IPokeandPullRequest, IRequestedValues, IWorkbookValues, WorkbookServiceResponse } from 'app/services/models/Workbook'
import { OperationStatus } from 'app/services/OperationStatus'
import { genericFailure } from 'app/services/WebServiceBase'
import { WorkbookService } from 'app/services/WorkbookService'
import * as utils from 'app/utils'
import { getWebServicesConfig } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'

interface IPensionPayouts {
  age: moment.Duration,
  payouts: utils.HashedNumbers<SimplePensionFormOfPaymentKinds>
}

interface INcpersRunCalculationsRequest {
  person: IRetirementPerson
  vals: INcpersPensionInputs
  when: moment.Moment
}

const payoutRange = 'j4:r8'

class NcpersActionCreator extends ActionCreator<INcpersOpState, INcpersOpAction> {

  protected endpoint = getWebServicesConfig().ncpersWorkbookEndpoint
  //protected endpoint = 'http://agave.thosdavidson.com:8001/retirementAppServer/'
  protected service = new WorkbookService(this.endpoint, 'ncpersWorkbook')

  getMonthlyPensionAmount(request: INcpersRunCalculationsRequest, wr: WorkbookServiceResponse): IMonthlyPensionPayments {
    const payouts = NcpersActions.getPensionResults(wr)
    const k = SimplePensionFormOfPayment[request.vals.formOfPayment]
    // the payout we are interested in is the fourth one
    return {
      amount: payouts[3].payouts[k],
      beginPaymentsAge: request.vals.beginPaymentsAge,
      kind: request.vals.formOfPayment,
      totalLumpSum: 0,
      totalVestingService: 0,
      totalPensionCredits: 0
    }
  }

  getPensionResults(wr: WorkbookServiceResponse): IPensionPayouts[] {
    const payouts = wr.Results.Inputs[payoutRange]
    // tslint:disable-next-line:no-console
    //console.log('payouts: ' + JSON.stringify(payouts, undefined, 4))

    return _(_.range(0, 4))
      .map((i) => {
        const s = 1 + i * 2
        const results: IPensionPayouts = {
          age: moment.duration(payouts[0][s + 1], 'years'),
          payouts: {}
        }
        const age = payouts[0][s + 1]
        for (let f = 1; f < 5; f++) {
          const amount = payouts[f][s + 1]
          // tslint:disable-next-line:no-console
          //console.log('age: ' + age + ', payout: ' + amount + SimplePensionFormOfPayment[f - 1])
          results.payouts[SimplePensionFormOfPayment[f - 1]] = amount
        }

        return results
      })
      .value()
  }

  xgetPriorPensionResults(vals: INcpersPriorPensionInputs, when: moment.Moment): IPensionPayouts {
    const factors: utils.HashedNumbers<SimplePensionFormOfPaymentKinds> = {
      singleLifeOnly: 1.0,
      FiftyPercentJointAndSurvivor: 0.90,
      seventyFivePercentJointAndSurvivor: 0.85,
      fullJointAndSurvivor: 0.80
    }
    const payouts: utils.HashedNumbers<SimplePensionFormOfPaymentKinds> = {}
    Object.keys(SimplePensionFormOfPayment)
      .forEach((item) => {
        const pensionAmount = vals.pensionAmount || 0
        payouts[item] = pensionAmount * factors[item]
      });
    return {
      age: vals.beginPaymentsAge,
      payouts
    }
  }

  issueRequest(request: INcpersRunCalculationsRequest): Promise<WorkbookServiceResponse> {

    const inputs: IWorkbookValues = {
      Inputs: {
        'c6:c13': [
          [request.vals.salaries[0]],
          [request.vals.salaries[1]],
          [request.vals.salaries[2]],
          [''],
          [''],
          [request.vals.yearsOfService],
          [SimplePensionFormOfPayment[request.vals.formOfPayment]],
          [request.vals.beginPaymentsAge.years()]
        ]
      }
    }

    const outputs: IRequestedValues = {
      Inputs: [
        payoutRange
        //'b2:r13'  // useful for debugging
      ]
    }

    const req: IPokeandPullRequest = { inputs, outputs }
    return this.service.pokeAndPull(this.getSharedVar('accessToken'), req)
  }

  /**
   * Run the Ncpers calculations workbook.
   *
   * @param person The person to run the calculations for
   *
   * @returns a WorkbookServiceResponse.
   */
  runCalculations(request: INcpersRunCalculationsRequest): Promise<WorkbookServiceResponse> {
    this.dispatch(this.ncpersOpStarting())
    const startTime = moment.utc()

    return this.issueRequest(request)
      .then((wr: WorkbookServiceResponse) => {
        const endTime = moment.utc()
        const elapsed = endTime.diff(startTime)
        // tslint:disable-next-line:no-console
        //console.log('It took ' + elapsed + 'ms for ncpers calculation')
        if (wr.IsError) {
          this.dispatch(this.ncpersOpFailed(wr))
        } else {
          if (!wr.Results) {
            // tslint:disable-next-line:no-debugger
            //debugger
            this.dispatch(this.ncpersOpFailed(wr))
          } else {
            this.dispatch(this.ncpersOpCompleted(wr))
          }
        }
        return wr
      })
      .catch((reason) => {
        this.dispatch(this.ncpersOpFailed(reason))
        return Promise.reject(reason)
      })
  }

  private ncpersOpStarting = (): INcpersOpAction => {
    return {
      type: ActionTypes.NCPERS_OP_STARTING,
      ncpers: { op: new OperationStatus(), results: undefined }
    }
  }

  private ncpersOpCompleted = (response: WorkbookServiceResponse): INcpersOpAction => {
    return {
      type: ActionTypes.NCPERS_OP_COMPLETED,
      ncpers: { op: OperationStatus.fromResponse(response), results: response.Results }
    }
  }

  private ncpersOpFailed(reason: any): INcpersOpAction {
    return {
      type: ActionTypes.NCPERS_OP_FAILED,
      ncpers: { op: genericFailure(reason), results: undefined }
    }
  }
}

const NcpersActions = new NcpersActionCreator()

export {
  INcpersPensionInputs,
  INcpersRunCalculationsRequest,
  NcpersActions,
  NcpersActionCreator,
  WorkbookServiceResponse
}



// WEBPACK FOOTER //
// ./src/app/redux/Customers/ncpers/actions/index.ts